import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { UserRole } from '../../../../shared/models/auth.model';
import { ImageModule } from 'primeng/image';

export interface NavItem {
  label: string;
  icon: string;
  route: string;
  isActive?: boolean;
  roles?: UserRole[];
}

@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, ImageModule],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
})
export class SideNavComponent implements OnInit {
  @Input() activeRoute: string = 'dashboard';
  @Input() isOpen: boolean = true;
  @Output() sidebarClose = new EventEmitter<void>();
  navItems: NavItem[] = [];
  isMobile: boolean = false;

  constructor(private authService: AuthService, private router: Router) {}

  ngOnInit(): void {
    this.setupNavigationItems();
    this.checkMobileView();
  }

  // 👇 Listen to resize and handle mobile/desktop behavior
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkMobileView();
  }

  private checkMobileView(): void {
    this.isMobile = window.innerWidth < 768;
    // Don't automatically change isOpen here - let parent components handle it
  }

  private setupNavigationItems(): void {
    const currentUser = this.authService.getCurrentUser();

    this.navItems = [
      {
        label: 'Dashboard',
        icon: 'pi pi-th-large',
        route: '/dashboard',
        isActive: true,
      },
      { label: 'Letters', icon: 'pi pi-envelope', route: '/letters' },
      { label: 'Routing Slips', icon: 'pi pi-sitemap', route: '/routing-slip' },
    ];

    if (this.authService.hasRole(UserRole.ADMIN)) {
      this.navItems.push({
        label: 'Users',
        icon: 'pi pi-users',
        route: '/users',
        roles: [UserRole.ADMIN],
      });
    }

    this.navItems.push({
      label: 'Account Settings',
      icon: 'pi pi-cog',
      route: '/account',
    });
  }

  isActive(route: string): boolean {
    return this.activeRoute === route;
  }

  isActiveRoute(route: string): boolean {
    return this.router.url === route;
  }

  canAccess(item: NavItem): boolean {
    if (!item.roles) return true;
    const currentUser = this.authService.getCurrentUser();
    return currentUser ? item.roles.includes(currentUser.role) : false;
  }

  onNavItemClick(): void {
    // Close sidebar on mobile when navigation item is clicked
    if (this.isMobile) {
      this.sidebarClose.emit();
    }
  }

  onOverlayClick(): void {
    // Close sidebar when overlay is clicked
    this.sidebarClose.emit();
  }

  onLogoClick(): void {
    // Navigate to dashboard when logo is clicked
    this.router.navigate(['/dashboard']);

    // Close sidebar on mobile when logo is clicked
    if (this.isMobile) {
      this.sidebarClose.emit();
    }
  }
}
